'use client'
import { Flame } from 'lucide-react'
import type { TierKey } from '@/components/phishtrainer/me/medallions/tier-palette'
import { TierMedallion, type MedallionState } from '@/components/phishtrainer/me/medallions/tier-medallion'
import { RayBurstBackdrop } from '@/components/phishtrainer/me/medallions/ray-burst-backdrop'
import { StarIcon } from '@/components/phishtrainer/me/medallions/star-icon'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/components/user-portal/learner-i18n'
import { WeekStatsStrip } from './week-stats-strip'
import { tierName } from './catalogue-text'

export interface TierHeroBandProps {
  userName: string
  currentStars: number
  currentTier: TierKey | null
  peakTier: TierKey | null
  nextTier: TierKey | null
  starsToNextTier: number
  progressToNextTier: number
  streakCurrent: number
  weekStats: { rounds: number; passed: number; perfect: number; starsEarned: number }
  tiers: Array<{ key: TierKey; name: string; threshold: number }>
  /** Optional href for the "How the game works" link below the progress bar. */
  howItWorksHref?: string
}

export function TierHeroBand(props: TierHeroBandProps) {
  const { userName, currentStars, currentTier, peakTier, nextTier, starsToNextTier, progressToNextTier, streakCurrent, weekStats, tiers } = props
  const { t } = useTranslation()
  const currentIdx = tiers.findIndex((tier) => tier.key === currentTier)
  const peakIdx = tiers.findIndex((tier) => tier.key === peakTier)
  const nextDef = nextTier ? tiers.find((tier) => tier.key === nextTier) : null

  return (
    <div data-hero-band className="relative overflow-hidden rounded-2xl border border-border bg-card">
      <RayBurstBackdrop activeIndex={currentIdx} totalTiers={tiers.length} />
      <div className="relative px-3 pt-5 pb-4 sm:px-6 sm:pt-7 sm:pb-5">
        {/* Greeting row */}
        <div className="mb-4 sm:mb-5 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary">{t('learner.cards.tierHero.welcomeBack')}</div>
            <div className="text-lg sm:text-2xl font-bold text-foreground truncate">{userName}</div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-muted border border-border px-2 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs text-muted-foreground shrink-0">
            <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-orange-500" />
            <span className="font-semibold text-foreground tabular-nums">{streakCurrent}</span>
            <span className="hidden sm:inline">{t('learner.cards.tierHero.streakLong', { count: streakCurrent })}</span>
            <span className="sm:hidden">{t('learner.cards.tierHero.streakShort', { count: streakCurrent })}</span>
          </div>
        </div>

        {/* 5 medallions row */}
        <div className="flex items-end justify-between gap-1 sm:gap-2 max-w-5xl mx-auto">
          {tiers.map((tier, i) => {
            const state: MedallionState = i < currentIdx ? 'past' : i === currentIdx ? 'active' : 'locked'
            const sizeClass =
              state === 'active' ? 'w-[72px] sm:w-28 md:w-36'
                : state === 'past' ? 'w-[44px] sm:w-20 md:w-[86px]'
                : 'w-[40px] sm:w-[72px] md:w-[82px]'
            return (
              <div key={tier.key} className="flex flex-col items-center flex-1 min-w-0">
                <TierMedallion tier={tier.key} state={state} className={sizeClass} />
                <div className="mt-1.5 sm:mt-2 text-center w-full px-0.5">
                  <div className={'text-[10px] sm:text-sm font-semibold tracking-wide truncate ' +
                    (state === 'active' ? 'text-foreground' : state === 'past' ? 'text-muted-foreground' : 'text-muted-foreground/60')
                  }>{tierName(t, tier.key, tier.name)}</div>
                  {state === 'active' && (
                    <div className="mt-0.5 sm:mt-1 inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 rounded-full bg-muted border border-border text-[10px] sm:text-xs text-foreground">
                      <StarIcon size={10} />
                      <span className="font-semibold tabular-nums">{currentStars}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Gradient progress bar */}
        <div className="max-w-5xl mx-auto mt-5 sm:mt-6 px-1 sm:px-6">
          <div className="relative h-2 sm:h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="absolute inset-y-0 start-0 rounded-full rtl-flip"
              style={{
                width: `${progressToNextTier * 100}%`,
                background: 'linear-gradient(90deg, #ec4899 0%, #a855f7 45%, #f59e0b 80%, #facc15 100%)',
              }}
            />
            {/* Tier markers sit on the inline-start side, like the medallion row
                above them, so they mirror with it in right-to-left. */}
            {tiers.map((tier, i) => (
              <div key={tier.key} className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-border"
                style={{ insetInlineStart: `${(i / (tiers.length - 1)) * 100}%` }} />
            ))}
          </div>
          <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[9px] sm:text-[10px] text-muted-foreground tabular-nums">
            {tiers.map((tier) => <span key={tier.key}>{tier.threshold}</span>)}
          </div>
          {currentTier == null ? (
            <p className="mt-2.5 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              {t('learner.cards.tierHero.noTier')}
            </p>
          ) : nextDef ? (
            <p className="mt-2.5 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              {richText(t('learner.cards.tierHero.starsToNext', { count: starsToNextTier }), {
                stars: (
                  <span className="font-semibold text-foreground inline-flex items-center gap-1">
                    <StarIcon size={12} /> {starsToNextTier}
                  </span>
                ),
                tier: <span className="font-semibold text-foreground">{tierName(t, nextDef.key, nextDef.name)}</span>,
              })}
            </p>
          ) : (
            <p className="mt-2.5 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              {richText(t('learner.cards.tierHero.topTier'), {
                hold: <span className="text-foreground font-semibold">{t('learner.cards.tierHero.holdIt')}</span>,
              })}
            </p>
          )}
          {peakIdx > currentIdx && (
            <p className="mt-1 text-center text-[10px] sm:text-xs text-amber-600 dark:text-amber-400">
              {t('learner.cards.tierHero.peak', { tier: tierName(t, tiers[peakIdx].key, tiers[peakIdx].name) })}
            </p>
          )}
          {props.howItWorksHref && (
            <div className="mt-2 text-center">
              <a href={props.howItWorksHref} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                {t('learner.cards.tierHero.howItWorks')}
              </a>
            </div>
          )}
        </div>

        {/* This-week stats strip */}
        <div className="mt-5 sm:mt-6 max-w-5xl mx-auto">
          <WeekStatsStrip stats={weekStats} />
        </div>
      </div>
    </div>
  )
}
